import React from 'react';
import { CheckIcon, BrainIcon, ShieldCheckIcon, SparklesIcon, ZapIcon } from 'lucide-react';
import { CtaButton } from '../components/CtaButton';
import { FaqItem } from '../components/FaqItem';
import { bonuses, faqs, features, offerChecklist, outcomes, painPoints } from '../data/content';
const EBOOK_COVER_URL = 'https://cdn.magicpatterns.com/uploads/t2rPN5ecyQ38VMy4nU7H2N/DONE_MOCKUP.png';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/1roViPmDXjaiz1sGkRNVUW/Logo_IG.jpg';
const phases = [{
  label: 'Reset',
  description: 'Reprogram guilt and the "lazy" identity.'
}, {
  label: 'Activate',
  description: 'Break paralysis in under 60 seconds.'
}, {
  label: 'Shield',
  description: 'Build an unbreakable focus bubble.'
}, {
  label: 'Decide',
  description: 'Eliminate overthinking and overload.'
}, {
  label: 'Heal',
  description: 'Cut the shame and burnout spiral.'
}, {
  label: 'Automate',
  description: 'Turn the system into a permanent habit.'
}];
export function SalesPage() {
  return <div className="min-h-screen w-full overflow-x-hidden bg-brand-cream font-body text-slate-800" data-id="element-10" data-mp="3akrv1q7tia2s">

      {/* Top banner */}
      <header data-id="element-11" data-mp="35rozjcjhcqjy">
        <div className="bg-brand-medium px-4 py-2 text-center text-xs font-bold tracking-wide text-white sm:text-sm" data-id="element-12" data-mp="35qx6a8dcv4wu">
          <span aria-hidden="true" data-id="element-13" data-mp="2vutzgp7k6i3k">🌿 </span>Digital download — Instant access after purchase
        </div>
        <nav aria-label="Main navigation" className="flex justify-center bg-brand-cream px-5 py-4 sm:py-5" data-id="element-14" data-mp="3tic8m154tm83">
          <a href="#top" className="inline-flex items-center justify-center gap-3 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-medium" data-id="element-15" data-mp="3oizq1slan1bb">
            <img src={LOGO_URL} alt="" className="h-10 w-10 rounded-full bg-black object-contain ring-2 ring-brand-medium/20" data-id="element-16" data-mp="3jm99z59ho5pw" />
            <span className="font-display text-2xl font-bold tracking-tight text-brand-deep" data-id="element-17" data-mp="285rcd1u5nx1o">
              Prof. Kyra
            </span>
          </a>
        </nav>
      </header>

      <main id="top" data-id="element-18" data-mp="25ktmkb1qxn81">

        {/* Hero */}
        <section className="bg-brand-deep px-5 py-12 text-center text-white sm:py-16 lg:py-20" data-id="element-19" data-mp="25m4qqbz3swzo">
          <div className="mx-auto max-w-4xl" data-id="element-20" data-mp="2aiv2in3vl6fa">
            <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-7xl" data-id="element-21" data-mp="2ahk28x9n2gsx">
              Your Brain Is Not Broken.{' '}
              <span className="text-brand-gold" data-id="element-22" data-mp="20kfh90rj4rdp">It Just Needs a System Built for It.</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/85 sm:text-xl" data-id="element-23" data-mp="1msr5iyl704x9">
              <strong className="font-black text-white" data-id="element-24" data-mp="1rmlm3t5i43s4">DONE.</strong> is the only method that turns paralysis, endless scrolling, and guilt into real action — in under 60 seconds.{' '}
              <strong className="font-black text-white" data-id="element-25" data-mp="1hw0pucdavbcd">Now with the full 6×1 system!</strong>
            </p>
            <img src={EBOOK_COVER_URL} alt="The complete DONE. 6×1 system — six volumes plus 5 bonuses included" className="mx-auto mt-6 w-full max-w-2xl bg-transparent object-contain" data-id="element-26" data-mp="13vzm8p5nhpt7" />
            <div className="mt-7" data-id="element-27" data-mp="p9k4jnmaidhf">
              <CtaButton label="YES, I WANT TO CHANGE!" data-id="element-28" data-mp="palt8uw8ayhm" />
              
            </div>
          </div>
        </section>

        {/* Social proof bar */}
        <section aria-label="Social proof" className="bg-[#e8ddd0] px-5 py-4 text-center text-sm font-bold sm:text-base text-brand-deep" data-id="element-29" data-mp="yai8k5wqal00">
          <span aria-hidden="true" data-id="element-30" data-mp="tcgu5ya8z9uv">🎯 </span>Trusted by adults with ADHD who are done fighting their own brain
        </section>

        {/* Pain points */}
        <section id="pain-points" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-31" data-mp="137h5ew7s161w">
          <div className="mx-auto max-w-5xl" data-id="element-32" data-mp="1h0glazbh129z">
            <div className="mx-auto max-w-2xl text-center" data-id="element-33" data-mp="1gz5cgl3654c0">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-34" data-mp="1cy3gcd4jt8e9">
                You are not alone
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-35" data-mp="1qw0ehdynhmv1">
                Does any of this sound familiar?
              </h2>
            </div>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2 sm:gap-5" role="list" data-id="element-36" data-mp="25z1te3fnbhlp">
              {painPoints.map(point => <li key={point.text} className="flex items-start gap-4 rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-37" data-mp="2a0d99h8pwogp">
                  <span className="mt-0.5 text-2xl" aria-hidden="true" data-id="element-38" data-mp="2jssr8ek0ld1l">{point.emoji}</span>
                  <span className="text-base leading-relaxed text-slate-700" data-id="element-39" data-mp="1n2s56nb15anz">{point.text}</span>
                </li>)}
            </ul>
            <blockquote className="mx-auto mt-10 max-w-3xl border-l-4 border-brand-medium pl-5 font-display text-xl font-bold italic leading-relaxed text-brand-deep sm:text-2xl" data-id="element-40" data-mp="1r4n63qp1tm18">
              "It is not a lack of willpower. It is a lack of biological architecture for your type of brain."
            </blockquote>
            <p className="mx-auto mt-5 max-w-3xl text-center text-base leading-relaxed text-slate-600" data-id="element-41" data-mp="37hvj0f8d4m9t">
              78% of adults with ADHD repeatedly fail with standard productivity systems because those systems were designed for neurotypical brains. Your brain needs urgency, challenge, and interest to activate — and no generic planner gives you that.
            </p>
          </div>
        </section>

        {/* Solution */}
        <section id="solution" className="bg-brand-deep px-5 py-12 text-white sm:py-16" data-id="element-42" data-mp="m4ymtww772b4">
          <div className="mx-auto max-w-3xl text-center" data-id="element-43" data-mp="m34cfcijysla">
            <BrainIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-44" data-mp="i44zb4mo3pir" />
            <p className="mt-5 text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-45" data-mp="d38ver2bg3dc">The solution</p>
            <h2 className="mt-3 font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl" data-id="element-46" data-mp="85z2v2jmrbla">
              You don&apos;t need more willpower. You need a real system.
            </h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-white/85" data-id="element-47" data-mp="22hxch5luq9hp">
              <p data-id="element-48" data-mp="1xl6u2bmkr1t4">
                DONE. is not another habit journal. It is a 6-phase protocol that hacks the biology of your ADHD brain so that moving from "blocked" to "executing" depends on design, not motivation.
              </p>
              <p data-id="element-49" data-mp="mzkn07wnq3i">
                <strong className="font-bold text-white" data-id="element-50" data-mp="nhjm4lycif4">DONE.</strong> does not ask you to be a different person. It gives you the environment, scripts, and protocols so your current brain produces consistent, valuable, sustainable results.
              </p>
            </div>
            <div className="mt-8 grid gap-3 text-left sm:grid-cols-2" data-id="element-51" data-mp="em8w5ml61w15">
              {phases.map(phase => <div key={phase.label} className="rounded-xl border border-white/15 bg-white/5 p-4" data-id="element-52" data-mp="jk0rkql0s0a3">
                  <p className="text-sm font-black uppercase tracking-wide text-brand-gold" data-id="element-53" data-mp="5khssoe3m1ag">{phase.label}</p>
                  <p className="mt-1 text-sm leading-relaxed text-white/80" data-id="element-54" data-mp="9ofsp1b8zg4k">{phase.description}</p>
                </div>)}
            </div>
          </div>
        </section>

        {/* What's inside */}
        <section id="inside" className="bg-[#ede8df] px-5 py-12 sm:py-16" data-id="element-55" data-mp="2uahaw6b7lzeb">
          <div className="mx-auto max-w-6xl" data-id="element-56" data-mp="2mrroi877nfa5">
            <div className="mx-auto max-w-2xl text-center" data-id="element-57" data-mp="2htztv6f6lf29">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-58" data-mp="2htgenxgx14rx">
                300 pages designed for your brain
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-59" data-mp="2rrw29qgqhvwo">
                The 6 Volumes of the System
              </h2>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-60" data-mp="1z408atijui3l">
              {features.map((feature, index) => <article key={feature.title} className="rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-61" data-mp="2dsmlaokpexrn" data-mpi={index}>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-medium font-display text-lg font-bold text-white" data-id="element-62" data-mp="2903au0hbcir2">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-63" data-mp="utnse6iy40t5">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-xs font-black uppercase tracking-wide text-brand-medium" data-id="element-64" data-mp="ztafr8shmxfo">
                    {feature.pain}
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600" data-id="element-65" data-mp="18tyiv8gcfdds">
                    {feature.description}
                  </p>
                </article>)}
            </div>
          </div>
        </section>

        {/* Bonuses */}
        <section id="bonuses" className="bg-[#f0e8dc] px-5 py-12 sm:py-16" data-id="element-66" data-mp="1iq1wpbkk50gk">
          <div className="mx-auto max-w-5xl" data-id="element-67" data-mp="1nje9mofvjith">
            <div className="mx-auto max-w-2xl text-center" data-id="element-68" data-mp="1nkpbgipreghy">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-69" data-mp="1sg4oj8fy08db">
                Included at no extra cost
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-70" data-mp="21huq373jgnot">
                🎁 5 Free Bonuses Included
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-71" data-mp="3ly293mu63482">
                Everything you need to start executing today.
              </p>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2" data-id="element-72" data-mp="3d09smfcplqj3">
              {bonuses.map(bonus => <article key={bonus.title} className="rounded-r-2xl border-l-4 border-brand-medium bg-white px-5 py-5 shadow-sm sm:px-6" data-id="element-73" data-mp="381fbriyayc1m">
                  <div className="flex items-start gap-4" data-id="element-74" data-mp="8ncxu037sra7">
                    <span className="text-3xl" aria-hidden="true" data-id="element-75" data-mp="4kq1pmzigk77">{bonus.emoji}</span>
                    <div data-id="element-76" data-mp="3vxjc1wi1b40c">
                      <span className="inline-block rounded-full bg-brand-gold px-3 py-1 text-xs font-black uppercase tracking-wide text-brand-deep" data-id="element-77" data-mp="3vx1g70zaarsy">
                        {bonus.tag}
                      </span>
                      <h3 className="mt-3 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-78" data-mp="sakfc6rnmcsi">
                        {bonus.title}
                      </h3>
                      <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-79" data-mp="31faz5c1adev7">
                        {bonus.description}
                      </p>
                    </div>
                  </div>
                </article>)}
            </div>
          </div>
        </section>

        {/* Author */}
        <section id="author" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-80" data-mp="36c9rbp1e3bqr">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-7 text-center md:flex-row md:text-left" data-id="element-81" data-mp="3kckiyuaiwucz">
            <img src={LOGO_URL} alt="Prof. Kyra logo" className="h-40 w-40 flex-none rounded-full border-8 border-white bg-black object-contain shadow-lg" data-id="element-82" data-mp="3fcznitukb450" />
            <div data-id="element-83" data-mp="3p3u87ts5tvoc">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-84" data-mp="3p3aqoe6f99au">
                Created with care
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-85" data-mp="6yh8g867eobw">
                Meet Prof. Kyra
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-slate-700" data-id="element-86" data-mp="lkmy7gkyljfc">
                Prof. Kyra creates practical, science-informed digital guides designed to solve real everyday problems. Every resource is built to give real tools — not just information.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-87" data-mp="gljsdtx5n96b">
                DONE. was created for people who deserve a clear, calm system for managing their ADHD brain without feeling overwhelmed or ashamed.
              </p>
            </div>
          </div>
        </section>

        {/* Outcomes */}
        <section id="outcomes" className="bg-[#ede8df] px-5 py-12 sm:py-16" data-id="element-88" data-mp="3p253phd9eecl">
          <div className="mx-auto max-w-4xl text-center" data-id="element-89" data-mp="3u0pyh19qs5tn">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-90" data-mp="3u19hklb4p099">
              What you will achieve
            </p>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-91" data-mp="3kzrzwyi2oqig">
              After DONE.
            </h2>
            <p className="mx-auto mt-5 max-w-3xl font-display text-xl font-bold italic leading-relaxed text-brand-deep" data-id="element-92" data-mp="1qle4v8bxbngm">
              "Consistency is not a personality trait. It is the result of correct design."
            </p>
            <ul className="mt-8 grid gap-4 text-left sm:grid-cols-2" role="list" data-id="element-93" data-mp="1grniqa39rp43">
              {outcomes.map(outcome => <li key={outcome} className="flex items-start gap-3 rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-94" data-mp="29ip26ndzw6tf">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-olive" aria-hidden="true" data-id="element-95" data-mp="24knlua7yelln" />
                  <span className="text-base leading-relaxed text-slate-700" data-id="element-96" data-mp="20gxwvjnda203">{outcome}</span>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Offer */}
        <section id="offer" className="bg-brand-deep px-5 py-12 sm:py-16" data-id="element-97" data-mp="p33yqhoi9f8n">
          <div className="mx-auto max-w-xl rounded-3xl bg-white p-7 text-center shadow-2xl sm:p-8" data-id="element-98" data-mp="k40sitwsh2z3">
            <SparklesIcon className="mx-auto h-9 w-9 text-brand-medium" aria-hidden="true" data-id="element-99" data-mp="2vrco1xldj323" />
            <h2 className="mt-4 font-display text-3xl font-bold text-brand-deep sm:text-4xl" data-id="element-100" data-mp="30q77n1r60vn9">
              Everything You Get Today
            </h2>
            <p className="mt-2 font-display text-2xl font-bold text-brand-deep sm:text-3xl" data-id="element-101" data-mp="34qzh9w3y92k2">
              The DONE. System
            </p>
            <ul className="mt-7 space-y-3 text-left" role="list" data-id="element-102" data-mp="39kc01ply6f4w">
              {offerChecklist.map(item => <li key={item} className="flex items-start gap-3 text-[15px] leading-snug text-slate-700" data-id="element-103" data-mp="3ejyeu3hfc3ft">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-olive" aria-hidden="true" data-id="element-104" data-mp="3ofs1ifouxdbb" />
                  <span data-id="element-105" data-mp="3shdd7xpnnljx">{item}</span>
                </li>)}
            </ul>
            <div className="mt-8 flex flex-col items-center border-t border-brand-medium/15 pt-7 text-center" data-id="element-106" data-mp="3sf97rbc3yehj">
              <p className="text-base font-bold text-slate-500 line-through" data-id="element-107" data-mp="1u43km7z2f0oa">59.99 USD</p>
              <p className="mt-1 font-display text-5xl font-bold leading-none text-brand-deep" data-id="element-108" data-mp="1y5f2frbp6hlx">
                23.99 <span className="align-middle text-2xl" data-id="element-109" data-mp="12v0e2d8d2eha">USD</span>
              </p>
              <p className="mt-2 text-sm font-bold text-slate-600" data-id="element-110" data-mp="yratdyut4h1j">
                One-time payment — Yours forever
              </p>
            </div>
            <p className="mt-6 text-sm font-black text-brand-medium" data-id="element-111" data-mp="17rqvp4ztdi4t">
              Launch offer · Price goes up soon
            </p>
            <CtaButton label="YES, I WANT TO CHANGE!" className="mt-3 w-full" data-id="element-112" data-mp="k37jwfhk2n7z" />
            <p className="mt-4 text-xs font-bold tracking-wide text-slate-500" data-id="element-113" data-mp="f4mkgia4o7kl">
              🔒 Secure checkout <span aria-hidden="true" data-id="element-114" data-mp="ttizizalqkup">|</span> 📩 Instant delivery <span aria-hidden="true" data-id="element-115" data-mp="tuai99j3oo3k">|</span> 🛡️ 7-day guarantee
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-116" data-mp="1bepqhqr2vyo">
          <div className="mx-auto max-w-3xl" data-id="element-117" data-mp="3sira15mxjpit">
            <div className="text-center" data-id="element-118" data-mp="3shoh6s61rd09">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-119" data-mp="280fczf4outlk">
                Clear answers
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-120" data-mp="2cs74brvo53cr">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="mt-8 space-y-3" data-id="element-121" data-mp="230kpllk4gm59">
              {faqs.map(faq => <FaqItem key={faq.question} {...faq} data-id="element-122" data-mp="2qqx45f9vhh4t" />)}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section id="final-cta" className="bg-brand-deep px-5 py-12 text-center text-white sm:py-16" data-id="element-123" data-mp="2vq9p1uh6oe4r">
          <div className="mx-auto max-w-3xl" data-id="element-124" data-mp="2hr1lyqhwglva">
            <ZapIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-125" data-mp="2hscpcp7fnrlv" />
            <h2 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl" data-id="element-126" data-mp="2mnic1q8ntab0">
              Stop fighting your brain. Make it DONE.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/85" data-id="element-127" data-mp="1bag3nigprr1x">
              If you apply the system for 7 days and do not experience a real improvement in your ability to start, focus, and finish tasks, we will refund 100% of your investment. No questions. No hassle.
            </p>
            <p className="mt-5 text-sm font-black text-brand-gold" data-id="element-128" data-mp="3387w7sbw7d4t">
              Now with the full 6×1 system!
            </p>
            <CtaButton label="YES, I WANT TO CHANGE!" className="mt-3" data-id="element-129" data-mp="2z5uqv5125h8k" />
          </div>
        </section>

      </main>

      <footer className="bg-[#261510] px-5 py-10 text-center text-white/70" data-id="element-130" data-mp="2p9rdt44o3y6u">
        <ShieldCheckIcon className="mx-auto h-6 w-6 text-brand-gold" aria-hidden="true" data-id="element-131" data-mp="2p8gd5d6ir6jy" />
        <p className="mt-4 text-sm" data-id="element-132" data-mp="2kayb86qx5rlg">© 2026 Prof. Kyra · profkyra.com · profkyra@gmail.com</p>
        <nav aria-label="Footer navigation" className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm" data-id="element-133" data-mp="2ganvi2dbrmte">
          <a href="#privacy" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-134" data-mp="ih7dzglnollj">
            Privacy Policy
          </a>
          <a href="#terms" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-135" data-mp="1wd8njh553x18">
            Terms of Service
          </a>
          <a href="mailto:profkyra@gmail.com" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-136" data-mp="267ik7b147hhr">
            Contact
          </a>
        </nav>
        <p className="mx-auto mt-6 max-w-3xl text-xs leading-relaxed text-white/55" data-id="element-137" data-mp="1if1z0rm2z9e8">
          This product is for educational and informational purposes only. It does not replace professional medical diagnosis or treatment.
        </p>
      </footer>

    </div>;
}